import { create } from 'zustand';

export type PrintDocType = 'report1' | 'report2' | 'label1' | 'label2';
export type PrinterType = 'label' | 'general'; // ← 추가

export interface PrinterSetting {
  report1: string;
  report2: string;
  label1: string;
  label2: string;
}

// 프린터 타입 설정 (label1/label2 전용)
export interface PrinterTypeSetting {
  label1: PrinterType;
  label2: PrinterType;
}

export interface PrintDocConfig {
  key: PrintDocType;
  label: string;
  description: string;
  isLabel: boolean; // ← 라벨 문서 여부
}

export const PRINT_DOC_CONFIGS: PrintDocConfig[] = [
  { key: 'report1', label: '보고서1', description: '입고 전표 등 A4 보고서', isLabel: false },
  { key: 'report2', label: '보고서2', description: '출고 전표 등 A4 보고서', isLabel: false },
  { key: 'label1',  label: '라벨1',   description: '입고 LOT 라벨 (40x25mm)', isLabel: true },
  { key: 'label2',  label: '라벨2',   description: '출고 LOT 라벨 (40x25mm)', isLabel: true },
];

const STORAGE_KEY = 'mes_printer_settings';
const TYPE_STORAGE_KEY = 'mes_printer_type_settings';

const DEFAULT_SETTINGS: PrinterSetting = {
  report1: '',
  report2: '',
  label1: '',
  label2: '',
};

const loadFromStorage = (): PrinterSetting => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return { ...DEFAULT_SETTINGS };
    const raw = JSON.parse(saved) as Record<string, unknown>;
    // 키 이름 호환: label / label1·label2 혼용 방지
    return {
      report1: String(raw.report1 ?? ''),
      report2: String(raw.report2 ?? ''),
      label1: String(raw.label1 ?? raw.label ?? '').trim(),
      label2: String(raw.label2 ?? raw.label ?? '').trim(),
    };
  } catch {
    console.warn('[PrinterSetting] 설정 로드 실패, 기본값 사용');
  }
  return { ...DEFAULT_SETTINGS };
};

const loadTypeFromStorage = (): PrinterTypeSetting => {
  try {
    const saved = localStorage.getItem(TYPE_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    console.warn('[PrinterTypeSetting] 타입 설정 로드 실패, 기본값 사용');
  }
  // 기본값: 라벨 프린터
  return { label1: 'label', label2: 'label' };
};

interface PrinterSettingStore {
  settings: PrinterSetting;
  typeSettings: PrinterTypeSetting;

  setSetting: (docType: PrintDocType, printerName: string) => void;
  setTypeSetting: (docType: 'label1' | 'label2', printerType: PrinterType) => void;
  saveSettings: () => void;
  resetSettings: () => void;

  getPrinter: (docType: PrintDocType) => string;
  getPrinterType: (docType: 'label1' | 'label2') => PrinterType;
  // 라벨 출력 시 labelSize 반환 (일반 프린터면 undefined)
  getLabelSize: (docType: 'label1' | 'label2') => { width: number; height: number } | undefined;
}

export const usePrinterSettingStore = create<PrinterSettingStore>((set, get) => ({
  settings: loadFromStorage(),
  typeSettings: loadTypeFromStorage(),

  setSetting: (docType, printerName) =>
    set(state => ({
      settings: { ...state.settings, [docType]: printerName }
    })),

  setTypeSetting: (docType, printerType) =>
    set(state => ({
      typeSettings: { ...state.typeSettings, [docType]: printerType }
    })),

  saveSettings: () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(get().settings));
      localStorage.setItem(TYPE_STORAGE_KEY, JSON.stringify(get().typeSettings));
    } catch {
      console.error('[PrinterSetting] 저장 실패');
    }
  },

  resetSettings: () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TYPE_STORAGE_KEY);
    set({
      settings: { ...DEFAULT_SETTINGS },
      typeSettings: { label1: 'label', label2: 'label' },
    });
  },

  getPrinter: (docType) => get().settings[docType],
  getPrinterType: (docType) => get().typeSettings[docType],

  getLabelSize: (docType) => {
    const printerType = get().typeSettings[docType];
    // 라벨 프린터면 40x25mm, 일반 프린터면 undefined (A4)
    return printerType === 'label' ? { width: 70, height: 33 } : undefined;
  },
}));
